import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AGENCY_PROJECT_KIND_MAP,
  PROJECT_COLORS,
  PROJECT_HEALTH_MAP,
  type AgencyProjectView,
  type PortfolioView,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { WORDS, plural } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { Badge, EmptyState, ErrorState, Modal, Notice, PageHead } from '../../components/ui';
import { Avatar } from '../../components/People';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';

/**
 * المحافظ — خطوطُ العمل التي تنتمي إليها المشاريع.
 *
 * المحفظةُ تجميعُ قراءةٍ لا ملكية: «كلُّ عملاء التسويق»، «كلُّ العقارات»،
 * «البرودكشن». يقرأ المديرُ خطًّا كاملًا في صفٍّ واحد بدل أن يفتح عشرين
 * مشروعًا ليعرف أين يقف قسمٌ منها. وحذفُها يفكّ مشاريعها ولا يمسّها.
 *
 * وهي قبل المشاريع في القائمة عمدًا: الخطُّ أوّلًا ثمّ ما فيه. ومشروعٌ بلا
 * محفظةٍ يُعرض هنا صراحةً — لا يُترك ليضيع بين الصفوف: من لا خطَّ له لا
 * يدخل مقارنةً ولا يُقرأ في لوحة.
 */
export function AgencyPortfolios() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { atLeast } = useAuth();
  const [creating, setCreating] = useState(false);

  const portfolios = useQuery({
    queryKey: ['agency', 'portfolios'],
    queryFn: () => api<PortfolioView[]>('/agency/portfolios'),
  });
  const projects = useQuery({
    queryKey: ['agency', 'projects', false],
    queryFn: () => api<AgencyProjectView[]>('/agency/projects'),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['agency'] });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/agency/portfolios/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      refresh();
      toast('حُذفت المحفظة — مشاريعها باقية بلا محفظة');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحذف.'),
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api(`/agency/portfolios/${id}`, { method: 'PATCH', body: { name } }),
    onSuccess: refresh,
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  const rows = portfolios.data ?? [];
  const allProjects = (projects.data ?? []).filter((p) => !p.archived);
  const unassigned = allProjects.filter((p) => !p.portfolioId);
  const canEdit = atLeast('MANAGER');

  return (
    <div className="page">
      <PageHead
        title="المحافظ"
        subtitle="خطوطُ عملك: كلُّ مشروعٍ ينتمي إلى خطٍّ يُقرأ ويُقارَن — تسويق، عقارات، إنتاج"
        actions={
          canEdit ? (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={15} />
              محفظة جديدة
            </button>
          ) : undefined
        }
      />

      {portfolios.isError && (
        <ErrorState error={portfolios.error} onRetry={() => void portfolios.refetch()} />
      )}
      {(portfolios.isPending || projects.isPending) && (
        <div className="skeleton" style={{ height: 200 }} />
      )}

      {portfolios.data && rows.length === 0 && (
        <EmptyState
          title="لا محافظ بعد"
          text="المحفظةُ خطُّ عملٍ تُجمع فيه مشاريعُه: «عملاء التسويق»، «عملاء العقارات»، «الإنتاج». أنشئ خطوطك ثمّ انقل إليها مشاريعك — تُقرأ بعدها مقارنةً في الرئيسية."
          action={
            canEdit ? (
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                أنشئ محفظة
              </button>
            ) : undefined
          }
        />
      )}

      {/*
        مشاريعُ بلا خطّ — تُعدّ وتُسمّى، ولا تُسنَد من هنا.

        المحفظةُ تُختار عند إنشاء المشروع: ثمّ موضعٌ واحدٌ للقرار لا اثنان.
        وقائمةُ إسنادٍ في صفحة القراءة تجعل الصفحةَ شاشةَ ترتيبٍ بدل أن
        تكون شاشةَ موقف.
      */}
      {unassigned.length > 0 && rows.length > 0 && (
        <Notice
          kind="info"
          icon="folder"
          title={`${plural(unassigned.length, WORDS.project)} بلا محفظة`}
        >
          من لا خطَّ له لا يدخل مقارنةً ولا يُقرأ في لوحة. المحفظةُ تُختار عند إنشاء
          المشروع، والقائمُ منها يُنقل من نافذة تعديله.
          <span className="notice-names">
            {unassigned.slice(0, 12).map((p) => (
              <Link key={p.id} to={`/agency/projects/${p.id}`} className="notice-name">
                {p.name}
                <Icon name="chevronLeft" size={12} />
              </Link>
            ))}
          </span>
          {unassigned.length > 12 && (
            <p className="tiny mute">
              وباقي {plural(unassigned.length - 12, WORDS.project)}.
            </p>
          )}
        </Notice>
      )}

      <div className="pf-grid">
        {rows.map((pf) => {
          const mine = allProjects.filter((p) => p.portfolioId === pf.id);
          const pct = pf.taskCount ? Math.round((pf.doneCount / pf.taskCount) * 100) : 0;
          return (
            <section key={pf.id} className="pf-card">
              <span className="pf-bar" style={{ background: pf.color }} />

              <header className="pf-head">
                <Link
                  to={`/agency/portfolios/${pf.id}`}
                  className="pf-icon"
                  style={{ background: `${pf.color}1a`, color: pf.color }}
                  aria-label={`فتح محفظة ${pf.name}`}
                  title="فتح المحفظة"
                >
                  <Icon name="folder" size={18} />
                </Link>
                <input
                  className="pf-name"
                  defaultValue={pf.name}
                  disabled={!canEdit}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== pf.name) rename.mutate({ id: pf.id, name: v });
                  }}
                />
                <Link
                  to={`/agency/portfolios/${pf.id}`}
                  className="btn btn-sm btn-icon"
                  aria-label={`فتح محفظة ${pf.name}`}
                  title="فتح المحفظة"
                >
                  <Icon name="chevronLeft" size={15} />
                </Link>
                {canEdit && (
                  <button
                    className="btn btn-sm btn-icon"
                    onClick={() => {
                      if (window.confirm(`حذف محفظة «${pf.name}»؟ مشاريعها تبقى.`)) {
                        remove.mutate(pf.id);
                      }
                    }}
                    aria-label="حذف المحفظة"
                  >
                    <Icon name="trash" size={13} />
                  </button>
                )}
              </header>

              {pf.description && <p className="tiny mute">{pf.description}</p>}

              <div className="pf-stats">
                <span className="pf-stat">
                  <b className="num">{pf.projectCount}</b>
                  <span className="tiny mute">من المشاريع</span>
                </span>
                <span className="pf-stat">
                  <b className="num">{pct}٪</b>
                  <span className="tiny mute">أُنجز</span>
                </span>
                <span className={`pf-stat${pf.overdueCount > 0 ? ' is-late' : ''}`}>
                  <b className="num">{pf.overdueCount}</b>
                  <span className="tiny mute">متأخّرة</span>
                </span>
                <span className={`pf-stat${pf.atRisk + pf.offTrack > 0 ? ' is-warn' : ''}`}>
                  <b className="num">{pf.atRisk + pf.offTrack}</b>
                  <span className="tiny mute">في خطر</span>
                </span>
              </div>

              <div className="ag-progress" title={`${pf.doneCount}/${pf.taskCount}`}>
                <span
                  className="ag-progress-fill"
                  style={{ width: `${pct}%`, background: pf.color }}
                />
              </div>

              {mine.length === 0 ? (
                <p className="mute tiny">لا مشاريع فيها بعد — انقل إليها مشروعًا من الأعلى.</p>
              ) : (
                <ul className="ag-task-list">
                  {mine.map((p) => {
                    const ppct = p.taskCount ? Math.round((p.doneCount / p.taskCount) * 100) : 0;
                    return (
                      <li key={p.id}>
                        <Link
                          to={`/agency/projects/${p.id}`}
                          className="ag-task-row"
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          {p.accountName ? (
                            <Avatar name={p.accountName} src={p.accountAvatarUrl} size={24} />
                          ) : (
                            <span className="ag-dot" style={{ background: p.color }} />
                          )}
                          <span className="ag-task-title">{p.name}</span>
                          {p.health && (
                            <Badge kind={PROJECT_HEALTH_MAP[p.health]?.kind}>
                              {PROJECT_HEALTH_MAP[p.health]?.label}
                            </Badge>
                          )}
                          {p.overdueCount > 0 && (
                            <span className="ag-chip ag-chip-late num">{p.overdueCount}</span>
                          )}
                          <span className="ag-progress" style={{ width: '5rem', flex: 'none' }}>
                            <span
                              className="ag-progress-fill"
                              style={{ width: `${ppct}%`, background: p.color }}
                            />
                          </span>
                          <span className="tiny mute num">{ppct}٪</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          );
        })}
      </div>

      {creating && (
        <NewPortfolioModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function NewPortfolioModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string>(PROJECT_COLORS[4]);

  const create = useMutation({
    mutationFn: () =>
      api('/agency/portfolios', {
        method: 'POST',
        body: { name: name.trim(), color, description: description.trim() || null },
      }),
    onSuccess: onCreated,
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإنشاء.'),
  });

  return (
    <Modal title="محفظة جديدة" onClose={onClose}>
      <label className="field">
        <span className="label">اسم المحفظة</span>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="مثل: عملاء التسويق"
          autoFocus
        />
      </label>
      <label className="field">
        <span className="label">ما الذي يجمع مشاريعها؟</span>
        <input
          className="input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="مثل: كل حسابات السوشيال والإعلانات"
        />
      </label>
      <div className="field">
        <span className="label">اللون</span>
        <div className="ag-swatches">
          {PROJECT_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className={`ag-swatch${color === c ? ' is-active' : ''}`}
              style={{ background: c }}
              onClick={() => setColor(c)}
              aria-label={`اللون ${c}`}
            />
          ))}
        </div>
      </div>
      <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
        <button
          className="btn btn-primary"
          onClick={() => create.mutate()}
          disabled={name.trim().length < 2 || create.isPending}
        >
          أنشئ المحفظة
        </button>
        <button className="btn" onClick={onClose}>
          إلغاء
        </button>
      </div>
    </Modal>
  );
}
